import React, { useMemo, useCallback, useState } from 'react';
import { WarningFilled } from '@ant-design/icons';
import { SbTable, SbModal, SbButton, SbInput } from 'uiCore';
import { JD_ARCHIVE_PAGE_SIZE, JD_ARCHIVE_PAGE_SIZE_OPTIONS } from 'constants/jdArchive.constants';
import { ARCHIVE_DRAFTS_CONFIRM, jdHasDrafts } from 'Utils/sendAndPublish';
import {
  JdArchiveSectionStyled,
  JdArchiveTableWrapperStyled,
  ActionsCellStyled,
  ConfirmLabelStyled,
  ArchiveWarningBoxStyled,
} from './JdArchive.styles';

// The Role Name cell shows the JD title when there is one; filter on that same text.
const getRoleName = (r) => r.job_title_for_jd || r.job_title || '';

const JdArchiveTable = ({ rows, loading, onArchive, actioningId }) => {
  const [confirmRecord, setConfirmRecord] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  // A JD with drafts asks first (Save as Draft design): "This JD has drafts. Do you still wish to archive it?"
  // Archiving itself is unchanged (every version, drafts included; never the review cycles).
  const [draftsRecord, setDraftsRecord] = useState(null);

  const startArchive = useCallback((record) => {
    if (jdHasDrafts(record)) setDraftsRecord(record);
    else setConfirmRecord(record);
  }, []);

  const continueWithDrafts = useCallback(() => {
    setConfirmRecord(draftsRecord);
    setDraftsRecord(null);
  }, [draftsRecord]);

  const confirmRecordName = confirmRecord?.job_title_for_jd || confirmRecord?.job_title || '';
  const isConfirmMatch = confirmRecordName && confirmText.trim() === confirmRecordName;

  const closeConfirmModal = useCallback(() => {
    setConfirmRecord(null);
    setConfirmText('');
  }, []);

  const handleConfirmArchive = useCallback(async () => {
    await onArchive(confirmRecord);
    closeConfirmModal();
  }, [onArchive, confirmRecord, closeConfirmModal]);

  const columns = useMemo(
    () => [
      {
        title: 'REQ #',
        dataIndex: 'sb_requisition_number',
        render: (t) => t || '-',
      },
      {
        title: 'Role Name',
        key: 'job_title',
        filterValue: getRoleName,
        render: (_, r) => getRoleName(r) || '-',
      },
      {
        title: 'Business',
        dataIndex: 'business_unit',
        render: (t) => t || '-',
      },
      {
        title: 'Location',
        dataIndex: 'location',
        render: (t) => t || '-',
      },
      {
        title: 'Job Level',
        dataIndex: 'job_level',
        render: (t) => t || '-',
      },
      {
        title: 'Actions',
        key: 'action',
        width: 100,
        align: 'center',
        render: (_, record) => (
          <ActionsCellStyled>
            <SbButton buttonType="default" size="small" onClick={() => startArchive(record)}>
              Archive
            </SbButton>
          </ActionsCellStyled>
        ),
      },
    ],
    [startArchive],
  );

  return (
    <>
      <JdArchiveSectionStyled>
        <JdArchiveTableWrapperStyled>
          <SbTable
            columns={columns}
            dataSource={rows}
            loading={loading}
            rowKey={(record, index) => record?.sb_unique_id ?? index}
            sticky
            pagination={{
              defaultPageSize: JD_ARCHIVE_PAGE_SIZE,
              pageSizeOptions: JD_ARCHIVE_PAGE_SIZE_OPTIONS,
              showSizeChanger: true,
              size: 'small',
            }}
          />
        </JdArchiveTableWrapperStyled>
      </JdArchiveSectionStyled>

      <SbModal
        open={!!draftsRecord}
        title={ARCHIVE_DRAFTS_CONFIRM.title}
        onCancel={() => setDraftsRecord(null)}
        footer={
          <>
            <SbButton buttonType="default" onClick={() => setDraftsRecord(null)}>
              {ARCHIVE_DRAFTS_CONFIRM.cancelLabel}
            </SbButton>
            <SbButton buttonType="red" onClick={continueWithDrafts}>
              {ARCHIVE_DRAFTS_CONFIRM.continueLabel}
            </SbButton>
          </>
        }
        width={480}
        centered
        destroyOnClose
      >
        <div data-testid="archive-drafts-confirm">{ARCHIVE_DRAFTS_CONFIRM.body}</div>
      </SbModal>

      <SbModal
        open={!!confirmRecord}
        title="Archive Job Description"
        onCancel={closeConfirmModal}
        footer={
          <>
            <SbButton buttonType="default" onClick={closeConfirmModal} disabled={actioningId === confirmRecord?.sb_unique_id}>
              Cancel
            </SbButton>
            <SbButton
              buttonType="red"
              disabled={!isConfirmMatch || actioningId === confirmRecord?.sb_unique_id}
              loading={actioningId === confirmRecord?.sb_unique_id}
              onClick={handleConfirmArchive}
            >
              Archive JD
            </SbButton>
          </>
        }
        width={480}
        centered
        destroyOnClose
      >
        <div>
          You are about to archive <strong>{confirmRecordName}</strong>
          {confirmRecord?.sb_requisition_number ? ` (${confirmRecord.sb_requisition_number})` : ''}.
        </div>
        <ArchiveWarningBoxStyled>
          <WarningFilled />
          <span>
            It will be hidden from the Jobs Library and excluded from Compare and search. This cannot be
            undone. If an approval is pending, it will be cancelled and the approver and submitter will be
            notified.
          </span>
        </ArchiveWarningBoxStyled>
        <ConfirmLabelStyled>
          To confirm, type <strong>{confirmRecordName}</strong> below:
        </ConfirmLabelStyled>
        <SbInput
          placeholder={confirmRecordName}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
        />
      </SbModal>
    </>
  );
};

export default JdArchiveTable;
